import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, Circle, Clock, Mail, Phone } from "lucide-react";

const STATUS_STEPS = [
  { key: "New Request", label: "Request Received" },
  { key: "Pending Review", label: "Under Review" },
  { key: "More Information Needed", label: "More Info Needed" },
  { key: "Confirmed", label: "Confirmed" },
  { key: "Delivery Scheduled", label: "Delivery Scheduled" },
  { key: "Delivered", label: "Delivered" },
  { key: "Event in Progress", label: "In Progress" },
  { key: "Completed", label: "Completed" },
];

export default function CustomerPortal() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        setBooking(data);
      } catch (e) {
        setError("Booking not found");
      }
    })();
  }, [bookingId]);

  if (error) return <div className="section"><div className="mx-auto max-w-2xl text-center"><h1 className="h-display text-3xl">{error}</h1></div></div>;
  if (!booking) return <div className="section flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-pebble" /></div>;

  const idx = STATUS_STEPS.findIndex((s) => s.key === booking.status);

  return (
    <div className="section">
      <div className="mx-auto max-w-4xl">
        <div className="overline">Your Reservation Request</div>
        <h1 className="h-display mt-3 text-4xl md:text-5xl">#{booking.id.slice(0,8).toUpperCase()}</h1>
        <p className="mt-3 text-graphite">Thank you, {booking.customer_name}. We'll keep you updated at every step.</p>

        {/* Status pipeline */}
        <div className="mt-10 card-soft p-6 md:p-8">
          <div className="overline">Status</div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {STATUS_STEPS.map((s, i) => {
              const done = idx >= i && idx >= 0;
              const current = idx === i;
              return (
                <div key={s.key} className={`flex md:flex-col items-center md:items-start gap-3 rounded-2xl p-3 ${current ? "bg-champagne/50" : ""}`}>
                  {done ? <CheckCircle2 className="h-5 w-5 text-charcoal" /> : current ? <Clock className="h-5 w-5 text-charcoal" /> : <Circle className="h-5 w-5 text-mist" />}
                  <div className={`text-xs ${done || current ? "text-charcoal" : "text-mist"}`}>{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 card-soft p-8">
          <div className="overline">Event Details</div>
          <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Row k="Type" v={booking.event_type} />
            <Row k="Date" v={booking.event_date + (booking.event_end_date ? ` → ${booking.event_end_date}` : "")} />
            <Row k="Guests" v={booking.guest_count} />
            <Row k="Venue" v={booking.venue_name || "—"} />
            <Row k="Address" v={`${booking.event_address}, ${booking.event_city}`} />
            <Row k="Trailers requested" v={booking.trailers_requested === 2 ? "Two (6 stalls)" : booking.trailers_requested === 1 ? "One (3 stalls)" : "Undecided"} />
            <Row k="Assigned trailers" v={(booking.assigned_trailers || []).join(", ") || "Pending"} />
            <Row k="Add-ons" v={(booking.addons || []).join(", ") || "None"} />
          </dl>
        </div>

        <div className="mt-8 card-soft p-8">
          <div className="overline">Next Steps</div>
          <p className="mt-4 text-graphite leading-relaxed">
            Our team is reviewing your request. Once we confirm availability, we'll email a final quote and coordinate delivery, setup, and payment with you directly. Typical response time is within 24 hours.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm">
            <a href="mailto:info@southernutaheventrentals.com" className="inline-flex items-center gap-2 text-charcoal hover:text-pebble transition-colors">
              <Mail className="h-4 w-4" /> info@southernutaheventrentals.com
            </a>
            <a href="tel:+14352889909" className="inline-flex items-center gap-2 text-charcoal hover:text-pebble transition-colors">
              <Phone className="h-4 w-4" /> (435) 288-9909
            </a>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/booking" className="btn-ghost">Book another event</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between border-b border-stone last:border-0 py-2 gap-4">
      <dt className="text-graphite">{k}</dt>
      <dd className="text-charcoal text-right">{String(v)}</dd>
    </div>
  );
}
